/**
 * Owner: Masa Bayu
 * File: private-author-labels.ts
 * Date:Time: 2026-10-23 08:05
 * Description: Label dashboard author PRIVAT §467-C — en + ms (pola locale §381).
 *   §466 S4: semua kunci berISI DATA rekaan (kutipan peribadi, nama pencapaian,
 *   teks ulasan/notifikasi + masa, format jualan, lencana DEMO) dibuang. Yang
 *   tinggal hanya kopii tetap (motto, tagline) + label keadaan kosong yang
 *   jujur + salinan gerbang akses.
 */

import type { MarketplaceLanguage } from '../../home/marketplace/marketplace-locale';

export type PrivateNavId =
  | 'home'
  | 'dashboard'
  | 'myBooks'
  | 'createEbook'
  | 'manuscripts'
  | 'sales'
  | 'promotions'
  | 'reports'
  | 'profile'
  | 'help';
export type PrivateTabId = 'ringkasan' | 'jualan' | 'buku' | 'penerbitan' | 'analitik';
export type PrivateKpiId = 'sales' | 'royalty' | 'books' | 'followers';
export type PrivateShortcutId =
  'upload' | 'bookPlus' | 'megaphone' | 'report' | 'price' | 'receipt';

export interface PrivateAuthorLabels {
  readonly brand: string;
  readonly navAria: string;
  readonly homeAria: string;
  readonly nav: Readonly<Record<PrivateNavId, string>>;
  readonly sidebarMotto: string;
  readonly sidebarSub: string;
  readonly copyright: string;
  readonly searchPlaceholder: string;
  readonly notificationsAria: string;
  readonly chatAria: string;
  readonly accountAria: string;
  readonly role: string;
  readonly since: string;
  readonly editProfile: string;
  readonly editorButton: string;
  readonly editorButtonAria: string;
  readonly statBooks: string;
  readonly statFollowers: string;
  readonly statRating: string;
  readonly monthSalesLabel: string;
  readonly tagline: string;
  readonly tabsAria: string;
  readonly tabs: Readonly<Record<PrivateTabId, string>>;
  readonly periodAria: string;
  readonly periods: readonly string[];
  readonly kpiTitles: Readonly<Record<PrivateKpiId, string>>;
  readonly kpiDelta: string;
  readonly noData: string;
  readonly chartSalesTitle: string;
  readonly chartSalesAria: string;
  readonly chartMetricAria: string;
  readonly chartMetric: string;
  readonly chartEmpty: string;
  readonly currencyTitle: string;
  readonly currencyEmpty: string;
  readonly ordersTitle: string;
  readonly ordersEmpty: string;
  readonly catalogTitle: string;
  readonly catalogBooks: string;
  readonly catalogPaid: string;
  readonly catalogBuyers: string;
  readonly catalogPending: string;
  readonly booksTitle: string;
  readonly booksEmpty: string;
  readonly seeAll: string;
  readonly soldSuffix: string;
  readonly priceUnknown: string;
  readonly status: {
    readonly published: string;
    readonly unlisted: string;
  };
  readonly bookMenuAria: string;
  readonly bookMenuDetail: string;
  readonly bookMenuRead: string;
  readonly bookMenuRemove: string;
  readonly bookMenuRemoveConfirm: string;
  readonly carouselPrevAria: string;
  readonly carouselNextAria: string;
  readonly shortcutsTitle: string;
  readonly shortcutLabels: Readonly<Record<PrivateShortcutId, string>>;
  readonly notificationsTitle: string;
  readonly notificationsEmpty: string;
  readonly alertPending: string;
  readonly alertUnpriced: string;
  readonly alertPaid: string;
  readonly promoTitle: string;
  readonly promoText: string;
  readonly promoCta: string;
  readonly searchSubmit: string;
  readonly gateLoading: string;
  readonly gateSignedOutTitle: string;
  readonly gateSignedOutBody: string;
  readonly gateNotPublisherTitle: string;
  readonly gateNotPublisherBody: string;
  readonly gateErrorTitle: string;
  readonly retry: string;
  readonly loginCta: string;
  readonly publisherCta: string;
  readonly backHome: string;
}

export const PRIVATE_AUTHOR_LABELS: Readonly<Record<MarketplaceLanguage, PrivateAuthorLabels>> =
  Object.freeze({
    en: Object.freeze({
      brand: 'Author',
      navAria: 'Author dashboard sections',
      homeAria: 'Go to QX home',
      nav: Object.freeze({
        home: 'Home',
        dashboard: 'Dashboard',
        myBooks: 'My Books',
        createEbook: 'Create eBook',
        manuscripts: 'Manuscripts',
        sales: 'Sales & Royalties',
        promotions: 'Promotions',
        reports: 'Reports',
        profile: 'Profile & Settings',
        help: 'Help & Support',
      }),
      sidebarMotto: 'Write • Publish • Inspire',
      sidebarSub: 'Your story matters. We help you reach more readers.',
      copyright: '© 2026 QX. All rights reserved.',
      searchPlaceholder: 'Search books, manuscripts, reports, etc...',
      notificationsAria: 'Notifications',
      chatAria: 'Support chat',
      accountAria: 'Account menu',
      role: 'Publisher',
      since: 'Since',
      editProfile: 'Edit Profile',
      editorButton: 'Studio',
      editorButtonAria: 'Open the Studio',
      statBooks: 'Books Published',
      statFollowers: 'Followers',
      statRating: 'Avg. Rating',
      monthSalesLabel: 'Sales This Month',
      tagline: 'Today’s books, tomorrow’s legacy.',
      tabsAria: 'Dashboard sections',
      tabs: Object.freeze({
        ringkasan: 'Overview',
        jualan: 'Sales',
        buku: 'My Books',
        penerbitan: 'Publishing',
        analitik: 'Analytics',
      }),
      periodAria: 'Date range',
      periods: Object.freeze(['Last 3 Months', 'Last 6 Months', 'Last 12 Months']),
      kpiTitles: Object.freeze({
        sales: 'Total Sales',
        royalty: 'Royalties Received',
        books: 'Books Sold',
        followers: 'New Followers',
      }),
      kpiDelta: 'from last month',
      noData: 'No data yet',
      chartSalesTitle: 'Sales Performance',
      chartSalesAria: 'Monthly sales line chart',
      chartMetricAria: 'Chart metric',
      chartMetric: 'Total Sales',
      chartEmpty: 'No paid orders recorded yet.',
      currencyTitle: 'Revenue by Currency',
      currencyEmpty: 'Revenue appears once a paid order exists.',
      ordersTitle: 'Recent Transactions',
      ordersEmpty: 'No orders on your publications yet.',
      catalogTitle: 'Catalog Summary',
      catalogBooks: 'Publications',
      catalogPaid: 'Paid orders',
      catalogBuyers: 'Buyers',
      catalogPending: 'Awaiting payment',
      booksTitle: 'My Books',
      booksEmpty: 'You have not published a book yet.',
      seeAll: 'See All',
      soldSuffix: 'sold',
      priceUnknown: 'Price not set',
      status: Object.freeze({
        published: 'Live',
        unlisted: 'Price not set',
      }),
      bookMenuAria: 'Book actions',
      bookMenuDetail: 'View details',
      bookMenuRead: 'Open reader',
      bookMenuRemove: 'Remove',
      bookMenuRemoveConfirm: 'Remove "{title}" from the catalog? Readers will no longer see this book.',
      carouselPrevAria: 'Scroll books left',
      carouselNextAria: 'Scroll books right',
      shortcutsTitle: 'Quick Actions',
      shortcutLabels: Object.freeze({
        upload: 'Upload Manuscript',
        bookPlus: 'Add New Book',
        megaphone: 'Promo Campaign',
        report: 'View Reports',
        price: 'Manage Prices',
        receipt: 'View Transactions',
      }),
      notificationsTitle: 'Notifications',
      notificationsEmpty: 'Nothing needs your attention right now.',
      alertPending: 'orders are waiting for payment confirmation',
      alertUnpriced: 'publications still have no price',
      alertPaid: 'paid orders on your catalog',
      promoTitle: 'Grow Your Book Reach',
      promoText: 'Use promotions, discounts and special campaigns to lift sales.',
      promoCta: 'View Storefront',
      searchSubmit: 'Search the marketplace for',
      gateLoading: 'Loading your dashboard…',
      gateSignedOutTitle: 'Sign in required',
      gateSignedOutBody: 'The author dashboard uses your QX publisher session.',
      gateNotPublisherTitle: 'This account is not a publisher',
      gateNotPublisherBody:
        'This QX account is a reader. Become an author with the same email — do not register a second account.',
      gateErrorTitle: 'Dashboard could not be loaded',
      retry: 'Try again',
      loginCta: 'Sign in',
      publisherCta: 'Become an author',
      backHome: 'Back to home',
    }),
    ms: Object.freeze({
      brand: 'Author',
      navAria: 'Seksyen dashboard author',
      homeAria: 'Ke laman utama QX',
      nav: Object.freeze({
        home: 'Home',
        dashboard: 'Dashboard',
        myBooks: 'My Books',
        createEbook: 'Create eBook',
        manuscripts: 'Manuscripts',
        sales: 'Sales & Royalties',
        promotions: 'Promotions',
        reports: 'Reports',
        profile: 'Profile & Settings',
        help: 'Help & Support',
      }),
      sidebarMotto: 'Write • Publish • Inspire',
      sidebarSub: 'Your story matters. We help you reach more readers.',
      copyright: '© 2026 QX. All rights reserved.',
      searchPlaceholder: 'Search books, manuscripts, reports, etc...',
      notificationsAria: 'Notifikasi',
      chatAria: 'Sokongan chat',
      accountAria: 'Menu akaun',
      role: 'Penerbit',
      since: 'Sejak',
      editProfile: 'Edit Profil',
      editorButton: 'Studio',
      editorButtonAria: 'Buka Studio',
      statBooks: 'Buku Diterbitkan',
      statFollowers: 'Pengikut',
      statRating: 'Rating Purata',
      monthSalesLabel: 'Jualan Bulan Ini',
      tagline: 'Buku hari ini, legasi esok.',
      tabsAria: 'Seksyen dashboard',
      tabs: Object.freeze({
        ringkasan: 'Ringkasan',
        jualan: 'Jualan',
        buku: 'Buku Saya',
        penerbitan: 'Penerbitan',
        analitik: 'Analitik',
      }),
      periodAria: 'Julat tarikh',
      periods: Object.freeze(['3 Bulan Terakhir', '6 Bulan Terakhir', '12 Bulan Terakhir']),
      kpiTitles: Object.freeze({
        sales: 'Jumlah Jualan',
        royalty: 'Royalti Diterima',
        books: 'Buku Terjual',
        followers: 'Pengikut Baharu',
      }),
      kpiDelta: 'dari bulan lalu',
      noData: 'Tiada data lagi',
      chartSalesTitle: 'Prestasi Jualan',
      chartSalesAria: 'Carta garis jualan bulanan',
      chartMetricAria: 'Metrik carta',
      chartMetric: 'Jumlah Jualan',
      chartEmpty: 'Belum ada pesanan berbayar direkodkan.',
      currencyTitle: 'Hasil Mengikut Mata Wang',
      currencyEmpty: 'Hasil muncul sebaik pesanan dibayar.',
      ordersTitle: 'Transaksi Terbaru',
      ordersEmpty: 'Belum ada pesanan pada terbitan anda.',
      catalogTitle: 'Ringkasan Katalog',
      catalogBooks: 'Terbitan',
      catalogPaid: 'Pesanan berbayar',
      catalogBuyers: 'Pembeli',
      catalogPending: 'Menunggu pembayaran',
      booksTitle: 'Buku Saya',
      booksEmpty: 'Anda belum menerbitkan buku lagi.',
      seeAll: 'Lihat Semua',
      soldSuffix: 'jualan',
      priceUnknown: 'Harga belum ditetapkan',
      status: Object.freeze({
        published: 'Tersedia',
        unlisted: 'Harga belum ditetapkan',
      }),
      bookMenuAria: 'Tindakan buku',
      bookMenuDetail: 'Lihat butiran',
      bookMenuRead: 'Buka pembaca',
      bookMenuRemove: 'Buang',
      bookMenuRemoveConfirm: 'Buang "{title}" dari katalog? Pembaca tidak lagi nampak buku ini.',
      carouselPrevAria: 'Gelongsor buku kiri',
      carouselNextAria: 'Gelongsor buku kanan',
      shortcutsTitle: 'Pintasan Pantas',
      shortcutLabels: Object.freeze({
        upload: 'Muat Naik Manuskrip',
        bookPlus: 'Tambah Buku Baharu',
        megaphone: 'Kempen Promosi',
        report: 'Lihat Laporan',
        price: 'Urus Harga',
        receipt: 'Lihat Transaksi',
      }),
      notificationsTitle: 'Notifikasi',
      notificationsEmpty: 'Tiada apa-apa yang memerlukan tindakan anda sekarang.',
      alertPending: 'pesanan menunggu pengesahan pembayaran',
      alertUnpriced: 'terbitan belum mempunyai harga',
      alertPaid: 'pesanan berbayar pada katalog anda',
      promoTitle: 'Tingkatkan Jangkauan Buku Anda',
      promoText: 'Gunakan promosi, diskaun dan kempen khas untuk meningkatkan jualan.',
      promoCta: 'Lihat Kedai',
      searchSubmit: 'Cari di pasar untuk',
      gateLoading: 'Memuatkan dashboard anda…',
      gateSignedOutTitle: 'Log masuk diperlukan',
      gateSignedOutBody: 'Dashboard author memakai sesi penerbit QX anda.',
      gateNotPublisherTitle: 'Akaun ini bukan penerbit',
      gateNotPublisherBody:
        'Akaun QX ini ialah pembaca. Jadi penulis dengan e-mel yang sama — jangan daftar akaun kedua.',
      gateErrorTitle: 'Dashboard tidak dapat dimuatkan',
      retry: 'Cuba lagi',
      loginCta: 'Log Masuk',
      publisherCta: 'Jadi penulis',
      backHome: 'Kembali ke laman utama',
    }),
  });
